import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { FinanceManualChartItem, FinanceManualListRow } from '../../../../../core/models/finance/finance-manual-list.model';
import { MapperFinanceManualListService } from '../../../../../core/mappers/finance/mapper-finance-manual-list.service';
import { ApiFinanceManualService } from '../../../../../core/services/api/finance/api-finance-manual.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { RevenueAddFormComponent } from '../revenue-add-form/revenue-add-form.component';
import { ModalRevenueDetailComponent } from '../../../modals/finance/revenue/modal-revenue-detail/modal-revenue-detail.component';

@Component({
  selector: 'app-revenue-add',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, RevenueAddFormComponent, ModalRevenueDetailComponent],
  templateUrl: './revenue-add.component.html',
  styleUrl: './revenue-add.component.css',
})
export class RevenueAddComponent implements OnInit {
  private static readonly DEFAULT_DATE_RANGE_DAYS = 7;

  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiFinanceManualService);
  private readonly mapper = inject(MapperFinanceManualListService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  revenues: FinanceManualListRow[] = [];
  protected Math = Math;

  searchText = '';
  searchType: 'all' | 'receiptId' | 'feeName' | 'description' = 'all';
  isSearchTypeDropdownOpen = false;

  filterFeeType = '';
  filterFeeName = '';
  minFee: number | null = null;
  feeNameSearchText = '';
  feeTypeOptions: string[] = [];
  feeNameOptions: string[] = [];

  currentPage = 1;
  pageSize = 20;
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems = 0;
  totalPages = 1;

  endDate: string | null = null;
  startDate: string | null = null;

  showDashboard = true;
  dashboardStats: Array<{ title: string; subtitle: string; value: string; icon: string; color: string }> = [];
  recentActivity: Array<{ initial: string; name: string; timeAgo: string; info: string; color: string }> = [];
  dashboardData: FinanceManualChartItem[] = [];

  chartMode: 'daily' | 'monthly' = 'daily';
  dailySubMode: 'default' | 'custom' = 'default';
  customStartDate = '';
  customEndDate = '';
  chartType: 'line' | 'bar' = 'line';
  selectedMonthStart = new Date().toISOString().slice(0, 7);
  selectedMonthEnd = new Date().toISOString().slice(0, 7);
  yAxisLabels: number[] = [];
  currentMonthPoints = '';
  currentMonthAreaPoints = '';
  currentMonthDots: Array<{ x: number; y: number; label: string; subLabel: string; value: number; color: string }> = [];
  chartTotalCount = 0;

  isLoading = false;
  isDashboardLoading = false;
  lastFetchedTime: Date | null = null;
  isFormView = false;
  isDetailModalOpen = false;
  selectedRevenueId = 0;
  userId = 0;

  constructor() {
    this.applyDefaultDateRange();

    const userIdLocal = localStorage.getItem('userId');
    this.userId = userIdLocal ? Number(userIdLocal) : Number(this.cookieService.get('user_id'));

    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id || !this.userId) return;
      untracked(() => {
        this.currentPage = 1;
        this.loadTable();
        this.loadMarketingDashboard();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get menuId(): number {
    return this.permissionService.getMenuId(this.router.url) ?? 0;
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get filteredFeeNameOptions(): string[] {
    if (!this.feeNameSearchText) return this.feeNameOptions;
    const search = this.feeNameSearchText.toLowerCase();
    return this.feeNameOptions.filter((name) => name.toLowerCase().includes(search));
  }

  get currentSearchTypeLabel(): string {
    switch (this.searchType) {
      case 'receiptId':
        return 'เลขที่ใบเสร็จ';
      case 'feeName':
        return 'รายการรายได้';
      case 'description':
        return 'หมายเหตุ';
      default:
        return 'ค้นหาทั่วไป';
    }
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  ngOnInit(): void {
    if (!this.userId) {
      const userIdLocal = localStorage.getItem('userId');
      this.userId = userIdLocal ? Number(userIdLocal) : Number(this.cookieService.get('user_id'));
    }
    if (this.selectedSiteId && this.userId) {
      this.loadTable();
      this.loadMarketingDashboard();
    }
  }

  toggleFeeType(type: string): void {
    this.filterFeeType = this.filterFeeType === type ? '' : type;
    this.currentPage = 1;
    this.loadTable();
  }

  toggleFeeName(name: string): void {
    this.filterFeeName = this.filterFeeName === name ? '' : name;
    this.currentPage = 1;
    this.loadTable();
  }

  resetFeeNameFilter(): void {
    this.filterFeeName = '';
    this.feeNameSearchText = '';
    this.currentPage = 1;
    this.loadTable();
  }

  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  selectSearchType(type: 'all' | 'receiptId' | 'feeName' | 'description'): void {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
    this.currentPage = 1;
  }

  toggleDashboard(): void {
    this.showDashboard = !this.showDashboard;
  }

  formatDate(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  getTimeAgo(dateInput: string | Date | null | undefined): string {
    if (!dateInput) return '-';
    const date = new Date(dateInput);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' ปีที่แล้ว';
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' เดือนที่แล้ว';
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' วันที่แล้ว';
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' ชั่วโมงที่แล้ว';
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' นาทีที่แล้ว';
    return 'เพิ่งสร้าง';
  }

  onSearch(): void {
    this.currentPage = 1;
  }

  onClickSearch(): void {
    this.currentPage = 1;
    this.loadTable();
    this.loadMarketingDashboard();
  }

  onRefresh(): void {
    this.searchText = '';
    this.applyDefaultDateRange();
    this.filterFeeType = '';
    this.filterFeeName = '';
    this.minFee = null;
    this.searchType = 'all';
    this.currentPage = 1;
    this.dailySubMode = 'default';
    this.chartMode = 'daily';
    this.loadTable();
    this.loadMarketingDashboard();
  }

  onDateChange(): void {
    this.onClickSearch();
  }

  onMinFeeChange(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    this.pageSize = Number((event.target as HTMLSelectElement).value);
    this.currentPage = 1;
    this.loadTable();
  }

  navigateToCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Sidebar');
      return;
    }
    this.isFormView = true;
  }

  backToList(): void {
    this.isFormView = false;
  }

  onFormSaved(): void {
    this.isFormView = false;
    this.loadTable();
    this.loadMarketingDashboard();
  }

  openDetailModal(id: number): void {
    if (!id) return;
    this.selectedRevenueId = id;
    this.isDetailModalOpen = true;
  }

  closeDetailModal(): void {
    this.isDetailModalOpen = false;
    this.selectedRevenueId = 0;
  }

  setChartMode(mode: 'daily' | 'monthly'): void {
    this.chartMode = mode;
    if (mode === 'monthly') {
      const today = new Date();
      this.selectedMonthEnd = today.toISOString().slice(0, 7);
      const prev = new Date(today.getFullYear(), today.getMonth(), 1);
      this.selectedMonthStart = prev.toISOString().slice(0, 7);
    }
    this.processDashboardData();
  }

  setChartType(type: 'line' | 'bar'): void {
    this.chartType = type;
  }

  onChartMonthChange(): void {
    if (this.chartMode !== 'monthly') return;
    this.processDashboardData();
  }

  setDailySubMode(mode: 'default' | 'custom'): void {
    this.dailySubMode = mode;
    if (mode === 'custom') {
      if (!this.customStartDate) this.customStartDate = this.startDate ?? '';
      if (!this.customEndDate) {
        const s = new Date(this.customStartDate);
        const e = new Date(this.customStartDate);
        e.setDate(s.getDate() + 6);
        this.customEndDate = this.formatDate(e);
      }
      this.onCustomDateChange();
    } else {
      this.processDashboardData();
    }
  }

  get maxCustomDate(): string {
    if (!this.customStartDate) return '';
    const d = new Date(this.customStartDate);
    d.setDate(d.getDate() + 10);
    return this.formatDate(d);
  }

  resetCustomDate(): void {
    const e = new Date();
    const s = new Date();
    s.setDate(e.getDate() - 9);
    this.customEndDate = this.formatDate(e);
    this.customStartDate = this.formatDate(s);
    this.processDashboardData();
  }

  onCustomDateChange(): void {
    if (!this.customStartDate || !this.customEndDate) return;
    const s = new Date(this.customStartDate);
    const e = new Date(this.customEndDate);
    if (e < s) this.customEndDate = this.customStartDate;
    const diffDays = Math.ceil((new Date(this.customEndDate).getTime() - new Date(this.customStartDate).getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 10) {
      this.toast.warn('แสดงผลได้สูงสุด 10 วัน');
      const newEnd = new Date(s);
      newEnd.setDate(s.getDate() + 9);
      this.customEndDate = this.formatDate(newEnd);
    }
    this.processDashboardData();
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.revenues = [];
      this.totalItems = 0;
      this.totalPages = 1;
      return;
    }

    this.isLoading = true;
    this.api.getFinanceManualForTable({
      siteId,
      userId: this.userId,
      page: this.currentPage,
      pageSize: this.pageSize,
      searchField: this.searchText.trim() ? this.mapper.mapSearchField(this.searchType) : undefined,
      searchText: this.searchText.trim() || undefined,
      startDate: this.startDate || undefined,
      endDate: this.endDate || undefined,
      feeType: this.filterFeeType || undefined,
      feeName: this.filterFeeName || undefined,
      minFee: this.minFee && this.minFee > 0 ? this.minFee : undefined,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTableFromDto(res);
        this.revenues = mapped.items;
        this.totalItems = mapped.total;
        this.totalPages = mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.pageSize));
        this.lastFetchedTime = new Date();
        this.isLoading = false;
      },
      error: () => {
        this.revenues = [];
        this.totalItems = 0;
        this.totalPages = 1;
        this.isLoading = false;
        this.toast.error('ไม่สามารถโหลดข้อมูลรายได้ได้');
      },
    });
  }

  loadMarketingDashboard(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.resetDashboardView();
      return;
    }

    this.isDashboardLoading = true;
    this.api.getFinanceManualMarketingDashboard(
      siteId,
      this.userId,
      this.startDate || undefined,
      this.endDate || undefined
    ).subscribe({
      next: (res) => {
        const view = this.mapper.mapMarketingDashboard(res);
        this.dashboardStats = view.stats;
        this.dashboardData = view.chartItems;
        this.feeTypeOptions = view.filterOptions.feeTypes;
        this.feeNameOptions = view.filterOptions.feeNames;
        this.recentActivity = view.recentActivity.map((item) => ({
          initial: item.siteName ? item.siteName.charAt(0).toUpperCase() : '?',
          name: item.siteName || '-',
          timeAgo: this.getTimeAgo(item.paidDate),
          info: `${item.feeName} (${(item.fee || 0).toLocaleString()} ฿)`,
          color: this.getRandomColor(item.id),
        }));
        this.processDashboardData();
        this.isDashboardLoading = false;
      },
      error: () => {
        this.resetDashboardView();
        this.isDashboardLoading = false;
      },
    });
  }

  private applyDefaultDateRange(): void {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - RevenueAddComponent.DEFAULT_DATE_RANGE_DAYS);
    this.endDate = this.formatDate(end);
    this.startDate = this.formatDate(start);
  }

  private resetDashboardView(): void {
    this.dashboardStats = [];
    this.recentActivity = [];
    this.dashboardData = [];
    this.feeTypeOptions = [];
    this.feeNameOptions = [];
    this.chartTotalCount = 0;
    this.currentMonthPoints = '';
    this.currentMonthAreaPoints = '';
    this.currentMonthDots = [];
  }

  private processDashboardData(): void {
    let xLabels: Date[] = [];
    let dataPoints: number[] = [];

    if (this.chartMode === 'daily') {
      let start: Date;
      let end: Date;
      if (this.dailySubMode === 'custom' && this.customStartDate && this.customEndDate) {
        start = new Date(this.customStartDate);
        end = new Date(this.customEndDate);
      } else if (this.startDate && this.endDate) {
        start = new Date(this.startDate);
        end = new Date(this.endDate);
      } else {
        start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        end = new Date();
      }

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        xLabels.push(new Date(d));
        dataPoints.push(0);
      }

      this.dashboardData.forEach((item) => {
        if (!item.paidDate) return;
        const d = new Date(item.paidDate);
        const dDateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        if (dDateOnly >= startDateOnly && dDateOnly <= endDateOnly) {
          const idx = xLabels.findIndex((x) => x.toDateString() === dDateOnly.toDateString());
          if (idx !== -1) dataPoints[idx]++;
        }
      });
    } else {
      const [startYear, startMonth] = this.selectedMonthStart.split('-').map(Number);
      const [endYear, endMonth] = this.selectedMonthEnd.split('-').map(Number);
      const start = new Date(startYear, startMonth - 1, 1);
      const end = new Date(endYear, endMonth, 0);
      const diffDays = Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays <= 60) {
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          xLabels.push(new Date(d));
          dataPoints.push(0);
        }
        this.dashboardData.forEach((item) => {
          if (!item.paidDate) return;
          const d = new Date(item.paidDate);
          if (d >= start && d <= end) {
            const idx = xLabels.findIndex((x) => x.toDateString() === d.toDateString());
            if (idx !== -1) dataPoints[idx]++;
          }
        });
      } else {
        let temp = new Date(startYear, startMonth - 1, 1);
        while (temp <= end) {
          xLabels.push(new Date(temp));
          dataPoints.push(0);
          temp.setMonth(temp.getMonth() + 1);
        }
        this.dashboardData.forEach((item) => {
          if (!item.paidDate) return;
          const d = new Date(item.paidDate);
          if (d < start || d > end) return;
          const idx = xLabels.findIndex((x) => x.getFullYear() === d.getFullYear() && x.getMonth() === d.getMonth());
          if (idx !== -1) dataPoints[idx]++;
        });
      }
    }

    this.chartTotalCount = dataPoints.reduce((a, b) => a + b, 0);
    const rawMax = Math.max(...dataPoints, 5);
    const maxVal = Math.ceil(rawMax / 4) * 4;
    this.yAxisLabels = [0, maxVal * 0.25, maxVal * 0.5, maxVal * 0.75, maxVal];

    const getCoordinate = (val: number, index: number, total: number) => {
      if (total <= 1) return { x: 50, y: 100 - (val / maxVal) * 100 };
      const x = 2 + (index / (total - 1)) * 96;
      const y = 100 - (val / maxVal) * 100;
      return { x, y };
    };

    const pointsObj = dataPoints.map((val, i) => getCoordinate(val, i, dataPoints.length));
    this.currentMonthPoints = pointsObj.map((p) => `${p.x},${p.y}`).join(' ');

    const getThaiMonth = (monthIndex: number) => {
      const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
      return months[monthIndex];
    };

    this.currentMonthDots = pointsObj.map((p, i) => {
      const date = xLabels[i];
      let label = `${date.getDate()}`;
      let subLabel = '';
      if (date.getDate() === 1 || i === 0) subLabel = getThaiMonth(date.getMonth());
      if (this.chartMode === 'monthly' && dataPoints.length <= 12 && xLabels.length > 31) {
        label = getThaiMonth(date.getMonth());
        subLabel = `${date.getFullYear() + 543}`.slice(2);
      }
      return { x: p.x, y: p.y, label, subLabel, value: dataPoints[i], color: this.getChartColor(i) };
    });

    if (pointsObj.length > 0) {
      this.currentMonthAreaPoints = `${pointsObj[0].x},100 ${this.currentMonthPoints} ${pointsObj[pointsObj.length - 1].x},100`;
    } else {
      this.currentMonthAreaPoints = '';
    }
  }

  private getChartColor(index: number): string {
    const colors = ['#fbbf24', '#f59e0b', '#34d399', '#60a5fa', '#f87171', '#818cf8', '#a78bfa', '#f472b6'];
    return colors[index % colors.length];
  }

  private getRandomColor(id: number): string {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    return colors[id % colors.length] || 'bg-blue-500';
  }
}
